import { Component, inject, OnDestroy, OnInit, signal } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { combineLatest, interval, Observable, startWith, Subject, switchMap, tap } from "rxjs";
import { MarketService } from "../../../../../../../../shared/src/lib/services/market.service";
import { LiveUpdateService } from "../../../../../../../../shared/src/lib/services/live-update.service";
import { BetPlacementService } from "../../../../../../../../shared/src/lib/services/bet-placement.service";
import { LoaderService } from "../../../../../../../../shared/src/lib/services/loader.service";
import { MarketOpsService } from "../../../market-layout/market/market-ops.service";
import { AsyncPipe } from "@angular/common";
import { map, takeUntil } from "rxjs/operators";
import { SportHighlightsComponent } from "../../../home/sport-highlights/sport-highlights.component";
import { InPlayLoaderComponent } from "../../../in-play/in-play-markets/in-play-loader/in-play-loader.component";
import { StatusAlertComponent } from "../../../../../components/status-alert/status-alert.component";

@Component({
  selector: "app-ip-markets-tab",
  standalone: true,
  imports: [SportHighlightsComponent, InPlayLoaderComponent, StatusAlertComponent, AsyncPipe],
  templateUrl: "./ip-markets-tab.component.html",
  styleUrl: "./ip-markets-tab.component.scss",
})
export class IpMarketsTabComponent implements OnInit, OnDestroy {
  readonly destroy$ = new Subject<void>();
  marketService = inject(MarketService);
  liveUpdateService = inject(LiveUpdateService);
  placementService = inject(BetPlacementService);
  loader = inject(LoaderService);
  marketOps = inject(MarketOpsService);
  route = inject(ActivatedRoute);

  markets: any[] = [];
  marketItems: string[] = [];
  $loading!: Observable<boolean>;
  isLoadingStatus = signal(false);
  sportId = "";

  sportIds: Record<string, string> = {
    soccer: "1",
    cricket: "4",
    tennis: "2",
    horse: "7",
    greyhound: "4339",
  };

  ngOnInit() {
    this.$loading = combineLatest([this.loader.isLoading("/bf/in-play-markets"), this.loader.isLoading("/v1/api/navigation_list")]).pipe(
      map(([loadingMarkets, loadingNavigationList]) => loadingMarkets || loadingNavigationList),
    );

    this.route.parent?.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const sport = params["sport"];
      this.sportId = this.sportIds[sport] || "";
      this.startFetching();
    });
  }

  startFetching() {
    interval(120000)
      .pipe(
        startWith(0),
        switchMap(() => this.getInPlayMarkets()),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  getInPlayMarkets() {
    this.isLoadingStatus.set(true);
    const params = { eventTypeIds: this.sportId };
    return this.marketService.fetchInPlayMarkets(params).pipe(
      tap({
        next: (response: any) => {
          const data = response?.data;
          let inPlayItems: any[] = [];
          let inPlayMessage: any[] = [];
          let openBetsFilterMarkets: any[] = [];
          if (data) {
            Object.keys(data).forEach((key) => {
              inPlayItems = [...inPlayItems, { key, value: data[key] }];
            });

            inPlayItems?.forEach((item) => {
              const markets = item?.value.map((market: any) => `bf_${market?.marketId}`);
              const marketIds = item?.value.map((market: any) => market?.marketId);
              openBetsFilterMarkets = [...openBetsFilterMarkets, ...marketIds];
              inPlayMessage = [...inPlayMessage, ...markets];
            });

            this.marketItems = inPlayMessage;
            this.placementService.setOpenBetFilterParams({
              marketIds: openBetsFilterMarkets?.join(","),
            });

            if (this.marketItems?.length > 0) {
              this.getMarketStatus({ market_ids: this.marketItems?.join(",") }, inPlayItems);
              this.marketOps.setTopics(this.marketItems);
            } else {
              this.markets = [];
              this.isLoadingStatus.set(false);
            }
          } else {
            this.markets = [];
            this.isLoadingStatus.set(false);
          }
        },
        error: (error: any) => {
          console.error(error);
          this.isLoadingStatus.set(false);
        },
      }),
    );
  }

  getMarketStatus(params: any, inPlayItems: any[]) {
    const qParams = { ...params, statuses: "2,3", return_snapshot: "Y" };

    this.marketService
      .fetchMarketStatus(qParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const { snapshot: snapshots = {}, statuses = {} } = response || {};
          if (inPlayItems?.length > 0) {
            const openedMarkets = statuses["2"] || [];
            const suspendedMarkets = statuses["3"] || [];

            this.markets = inPlayItems
              .map((item: any) => {
                let filteredMarkets = item.value
                  .filter((market: any) => {
                    const marketId = `bf_${market?.marketId}`;
                    return openedMarkets.includes(marketId) || suspendedMarkets.includes(marketId);
                  })
                  .map((market: any) => {
                    const marketId = `bf_${market?.marketId}`;
                    return {
                      ...market,
                      mc: snapshots[marketId],
                      eventName: market.event.name,
                    };
                  });

                return {
                  ...item,
                  value: filteredMarkets,
                };
              })
              .filter((item: any) => item.value.length > 0);
            this.isLoadingStatus.set(false);
            this.checkPlacement();
          }
        },
        error: (error) => {
          console.error("Failed to fetch market statuses:", error);
          this.isLoadingStatus.set(false);
        },
      });
  }

  checkPlacement() {
    let markets: any[] = [];
    this.markets.forEach((group: any) => {
      markets = [...markets, ...group.value];
    });

    this.placementService.currentPlacementConcernsMe(markets, "marketId");
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.marketOps.setTopics([]);
  }
}
